import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listAuditLogs, type AuditLogRow } from "@/lib/audit-log.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, ScrollText, RefreshCw, Search, Filter, X, ChevronDown } from "lucide-react";
import { TopFailedRecoveryIpsWidget } from "@/components/admin/TopFailedRecoveryIpsWidget";

export const Route = createFileRoute("/admin/audit")({
  head: () => ({
    meta: [
      { title: "Audit-log - AutoXChange Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuditLogPage,
});

const EVENT_LABELS: Record<string, { label: string; tone: "default" | "secondary" | "destructive" | "outline" }> = {
  login_success: { label: "Login OK", tone: "default" },
  login_failed: { label: "Login fejl", tone: "destructive" },
  logout: { label: "Logout", tone: "secondary" },
  mfa_enrolled: { label: "MFA aktiveret", tone: "default" },
  mfa_unenrolled: { label: "MFA fjernet", tone: "destructive" },
  mfa_challenge_success: { label: "MFA verificeret", tone: "default" },
  mfa_challenge_failed: { label: "MFA fejl", tone: "destructive" },
  recovery_codes_generated: { label: "Recovery genereret", tone: "default" },
  recovery_codes_revoked: { label: "Recovery slettet", tone: "secondary" },
  recovery_code_used: { label: "Recovery brugt", tone: "destructive" },
  role_assigned: { label: "Rolle tildelt", tone: "default" },
  role_changed: { label: "Rolle ændret", tone: "secondary" },
  role_removed: { label: "Rolle fjernet", tone: "destructive" },
  password_reset_sent: { label: "Password-reset sendt", tone: "secondary" },
  password_reset_completed: { label: "Password-reset gennemført", tone: "default" },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("da-DK", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function EventBadge({ type }: { type: string }) {
  const meta = EVENT_LABELS[type] ?? { label: type, tone: "outline" as const };
  return <Badge variant={meta.tone}>{meta.label}</Badge>;
}

function MetadataCell({ row }: { row: AuditLogRow }) {
  if (!row.metadata && !row.description) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-1 text-xs">
      {row.description && <div className="text-foreground">{row.description}</div>}
      {row.metadata && (
        <code className="block whitespace-pre-wrap break-all rounded bg-muted/50 px-2 py-1 font-mono text-[10px] text-muted-foreground">
          {JSON.stringify(row.metadata, null, 0)}
        </code>
      )}
    </div>
  );
}

function AuditLogPage() {
  const listFn = useServerFn(listAuditLogs);
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["audit-logs"],
    queryFn: () => listFn({ data: { limit: 200 } }),
    staleTime: 10_000,
  });

  const entries = data?.entries ?? [];
  const afterTypeFilter =
    selectedTypes.size === 0
      ? entries
      : entries.filter((e) => selectedTypes.has(e.eventType));
  const filtered = search.trim()
    ? afterTypeFilter.filter((e) => {
        const s = search.toLowerCase();
        return (
          e.eventType.toLowerCase().includes(s) ||
          e.actorEmail?.toLowerCase().includes(s) ||
          e.description?.toLowerCase().includes(s) ||
          e.ipAddress?.toLowerCase().includes(s)
        );
      })
    : afterTypeFilter;

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const clearTypes = () => setSelectedTypes(new Set());

  const filterLabel =
    selectedTypes.size === 0
      ? "Alle events"
      : selectedTypes.size === 1
        ? (EVENT_LABELS[Array.from(selectedTypes)[0]]?.label ?? Array.from(selectedTypes)[0])
        : `${selectedTypes.size} events valgt`;


  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <ScrollText size={24} className="text-accent" />
            Audit-log
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sikkerhedshændelser: login, MFA-ændringer, recovery-koder og rolle-ændringer.
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
          Genindlæs
        </Button>
      </div>

      <TopFailedRecoveryIpsWidget />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Seneste hændelser</CardTitle>
          <CardDescription>
            Viser op til 200 nyeste poster. Loggen er uforanderlig — poster kan ikke ændres eller slettes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Search size={16} className="text-muted-foreground" />
            <Input
              placeholder="Søg i email, event, IP, beskrivelse..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-muted-foreground" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[240px] justify-between font-normal"
                  >
                    <span className="truncate">{filterLabel}</span>
                    <ChevronDown size={14} className="ml-2 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Filtrér på event-type
                    </span>
                    {selectedTypes.size > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={clearTypes}
                      >
                        Ryd
                      </Button>
                    )}
                  </div>
                  <div className="max-h-[320px] overflow-y-auto py-1">
                    {Object.entries(EVENT_LABELS).map(([type, meta]) => {
                      const checked = selectedTypes.has(type);
                      return (
                        <label
                          key={type}
                          className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleType(type)}
                          />
                          <span className="flex-1">{meta.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              {selectedTypes.size > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={clearTypes}
                  title="Ryd filter"
                >
                  <X size={14} />
                </Button>
              )}
            </div>
            <span className="ml-auto text-sm text-muted-foreground">
              {filtered.length} af {entries.length}
            </span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 size={20} className="mr-2 animate-spin" /> Henter audit-log...
            </div>
          ) : error ? (
            <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
              Kunne ikke hente audit-log: {(error as Error).message}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {entries.length === 0
                ? "Ingen audit-poster endnu. De vises her efterhånden som hændelser logges."
                : "Ingen poster matcher søgningen."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Tidspunkt</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Bruger</TableHead>
                    <TableHead>Detaljer</TableHead>
                    <TableHead className="whitespace-nowrap">IP / Browser</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <EventBadge type={row.eventType} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.actorEmail ? (
                          <span className="text-foreground">{row.actorEmail}</span>
                        ) : (
                          <span className="text-muted-foreground">system</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <MetadataCell row={row} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div className="font-mono">{row.ipAddress ?? "—"}</div>
                        {row.userAgent && (
                          <div className="mt-0.5 max-w-[200px] truncate" title={row.userAgent}>
                            {row.userAgent}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
